import React, { useMemo } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  UserPlus, Upload, Download, Trash2, Search, ArrowUpAZ, ArrowDownAZ,
  UserCheck, ChevronLeft, ChevronRight,
} from 'lucide-react';

export const AdminVendorsTab = ({
  allVendors,
  allStalls,
  userFilter, setUserFilter,
  userSearch, setUserSearch,
  userSortOrder, setUserSortOrder,
  usersPerPage, setUsersPerPage,
  usersCurrentPage, setUsersCurrentPage,
  setShowAddUserModal,
  setShowBulkUploadVendors,
  setShowExportModal,
  setShowBulkDeleteModal,
  VendorCard,
}) => {
  // Helper to check if trial is expired
  const isTrialExpired = (user) => {
    if (user.subscription !== 'trial') return false;
    const trialStart = user.trial_start_date ? new Date(user.trial_start_date) : null;
    if (!trialStart) return false;
    const trialEnd = new Date(trialStart.getTime() + 7 * 24 * 60 * 60 * 1000);
    return new Date() > trialEnd;
  };

  // Users tab includes approved AND suspended (payment-failed) vendors, so admins
  // can still find and manage them. Pending/declined signups stay on the Pending tab.
  const approvedUsers = allVendors.filter(v => v.status === 'approved' || v.status === 'suspended');

  // Compute filtered and sorted users (memoized to prevent infinite loops)
  const filteredUsers = useMemo(() => approvedUsers
    .filter(v => {
      if (userFilter === 'all') return true;
      if (userFilter === 'vendor') return v.role === 'vendor';
      if (userFilter === 'admin') return v.role === 'admin';
      if (userFilter === 'suspended') return v.role === 'vendor' && v.status === 'suspended';
      if (userFilter === 'market-vendor') return v.role === 'vendor' && (v.vendor_type === 'market' || !v.vendor_type);
      if (userFilter === 'expo-vendor') return v.role === 'vendor' && v.vendor_type === 'expo';
      if (userFilter === 'festival-vendor') return v.role === 'vendor' && v.vendor_type === 'festival';
      if (userFilter === 'yearly') return v.role === 'vendor' && v.subscription === 'yearly';
      if (userFilter === 'monthly') return v.role === 'vendor' && v.subscription === 'monthly';
      if (userFilter === 'cash') return v.role === 'vendor' && (!v.subscription || v.subscription === 'cash');
      if (userFilter === 'manual') return v.role === 'vendor' && v.subscription === 'manual';
      if (userFilter === 'active-trial') return v.role === 'vendor' && v.subscription === 'trial' && !isTrialExpired(v);
      if (userFilter === 'expired-trial') return v.role === 'vendor' && v.subscription === 'trial' && isTrialExpired(v);
      return true;
    })
    .filter(v => {
      if (!userSearch) return true;
      const search = userSearch.toLowerCase();
      const stall = v.stall_id ? (allStalls || []).find(s => s._id === v.stall_id) : null;
      return (
        (v.name || '').toLowerCase().includes(search) ||
        (v.email || '').toLowerCase().includes(search) ||
        (v.company_name || '').toLowerCase().includes(search) ||
        (stall?.stallName || '').toLowerCase().includes(search) ||
        (v.first_name || '').toLowerCase().includes(search) ||
        (v.last_name || '').toLowerCase().includes(search) ||
        (v.phone || '').toLowerCase().includes(search)
      );
    })
    .sort((a, b) => {
      const stallA = a.stall_id ? (allStalls || []).find(s => s._id === a.stall_id) : null;
      const stallB = b.stall_id ? (allStalls || []).find(s => s._id === b.stall_id) : null;
      const nameA = (stallA?.stallName || a.company_name || a.name || a.email || '').toLowerCase();
      const nameB = (stallB?.stallName || b.company_name || b.name || b.email || '').toLowerCase();
      return userSortOrder === 'asc'
        ? nameA.localeCompare(nameB)
        : nameB.localeCompare(nameA);
    }), [approvedUsers, userFilter, userSearch, userSortOrder, allStalls]);

  const totalUsers = filteredUsers.length;

  const perPage = usersPerPage === 'all' ? totalUsers : parseInt(usersPerPage);
  const totalPages = Math.ceil(totalUsers / perPage) || 1;
  const startIndex = (usersCurrentPage - 1) * perPage;
  const paginatedUsers = usersPerPage === 'all'
    ? filteredUsers
    : filteredUsers.slice(startIndex, startIndex + perPage);

  return (
    <>
      <div className="flex flex-wrap gap-2 mb-4">
        <Button onClick={() => setShowAddUserModal(true)} className="bg-[#FFD12D] hover:bg-[#252525] text-[#252525] hover:text-[#FFD12D] text-sm">
          <UserPlus className="w-4 h-4 mr-2" /> Add New
        </Button>
        <Button onClick={() => setShowBulkUploadVendors(true)} className="bg-green-600 hover:bg-green-700 text-white text-sm">
          <Upload className="w-4 h-4 mr-2" /> Bulk Upload
        </Button>
        <Button onClick={() => setShowExportModal(true)} className="bg-blue-600 hover:bg-blue-700 text-white text-sm">
          <Download className="w-4 h-4 mr-2" /> Export
        </Button>
        <Button onClick={() => setShowBulkDeleteModal(true)} variant="destructive" className="text-sm">
          <Trash2 className="w-4 h-4 mr-2" /> Bulk Delete
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input type="text" placeholder="Search users by name, email, company..." value={userSearch} onChange={(e) => setUserSearch(e.target.value)} className="pl-9 dark:bg-[#1a1a1a] dark:text-white" />
        </div>
        <Button variant="outline" onClick={() => setUserSortOrder(userSortOrder === 'asc' ? 'desc' : 'asc')} className="flex items-center gap-2 border-gray-300 dark:border-gray-600">
          {userSortOrder === 'asc' ? <><ArrowUpAZ className="w-4 h-4" /> A-Z</> : <><ArrowDownAZ className="w-4 h-4" /> Z-A</>}
        </Button>
      </div>

      <div className="flex items-center gap-1 text-xs sm:text-sm border-b border-gray-200 dark:border-gray-700 pb-3 mb-4 overflow-x-auto no-scrollbar">
        <div className="flex items-center gap-1 flex-shrink-0">
        {[
          { key: 'all', label: `All (${approvedUsers.length})` },
          { key: 'vendor', label: `Vendor (${approvedUsers.filter(v => v.role === 'vendor').length})` },
          { key: 'admin', label: `Admin (${approvedUsers.filter(v => v.role === 'admin').length})` },
          { key: 'suspended', label: `Suspended (${approvedUsers.filter(v => v.role === 'vendor' && v.status === 'suspended').length})` },
          { key: 'market-vendor', label: `Market (${approvedUsers.filter(v => v.role === 'vendor' && (v.vendor_type === 'market' || !v.vendor_type)).length})` },
          { key: 'expo-vendor', label: `Expo (${approvedUsers.filter(v => v.role === 'vendor' && v.vendor_type === 'expo').length})` },
          { key: 'festival-vendor', label: `Festival (${approvedUsers.filter(v => v.role === 'vendor' && v.vendor_type === 'festival').length})` },
          { key: 'yearly', label: `Yearly (${approvedUsers.filter(v => v.role === 'vendor' && v.subscription === 'yearly').length})` },
          { key: 'monthly', label: `Monthly (${approvedUsers.filter(v => v.role === 'vendor' && v.subscription === 'monthly').length})` },
          { key: 'cash', label: `Cash (${approvedUsers.filter(v => v.role === 'vendor' && (!v.subscription || v.subscription === 'cash')).length})` },
          { key: 'manual', label: `Manual (${approvedUsers.filter(v => v.role === 'vendor' && v.subscription === 'manual').length})` },
          { key: 'active-trial', label: `Active Trial (${approvedUsers.filter(v => v.role === 'vendor' && v.subscription === 'trial' && !isTrialExpired(v)).length})` },
          { key: 'expired-trial', label: `Expired Trial (${approvedUsers.filter(v => v.role === 'vendor' && v.subscription === 'trial' && isTrialExpired(v)).length})` },
        ].map(({ key, label }, i, arr) => (
          <React.Fragment key={key}>
            <button onClick={() => { setUserFilter(key); setUsersCurrentPage(1); }}
              className={`whitespace-nowrap hover:text-[#FFD12D] transition-colors ${userFilter === key ? 'text-[#FFD12D] font-semibold' : 'text-gray-600 dark:text-gray-400'}`}>
              {label}
            </button>
            {i < arr.length - 1 && <span className="text-gray-400 dark:text-gray-600">|</span>}
          </React.Fragment>
        ))}
        </div>
        <div className="ml-auto flex items-center gap-2 flex-shrink-0">
          <span className="text-gray-600 dark:text-gray-400 text-xs sm:text-sm">Show:</span>
          <Select value={usersPerPage} onValueChange={(value) => { setUsersPerPage(value); setUsersCurrentPage(1); }}>
            <SelectTrigger className="w-20 sm:w-24 h-8 text-xs sm:text-sm dark:bg-[#1a1a1a] dark:text-white dark:border-gray-600"><SelectValue /></SelectTrigger>
            <SelectContent className="dark:bg-[#1a1a1a] dark:border-gray-700">
              <SelectItem value="10" className="dark:text-white">10</SelectItem>
              <SelectItem value="20" className="dark:text-white">20</SelectItem>
              <SelectItem value="50" className="dark:text-white">50</SelectItem>
              <SelectItem value="100" className="dark:text-white">100</SelectItem>
              <SelectItem value="all" className="dark:text-white">All</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {totalUsers > 0 && usersPerPage !== 'all' && (
        <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400 mb-3">
          <span>Showing {startIndex + 1} - {Math.min(startIndex + perPage, totalUsers)} of {totalUsers}</span>
          {totalPages > 1 && <span>Page {usersCurrentPage} of {totalPages}</span>}
        </div>
      )}

      {paginatedUsers.length === 0 ? (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          <UserCheck className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>No {userFilter === 'all' ? 'users' : userFilter + 's'} found{userSearch ? ` matching "${userSearch}"` : ''}</p>
        </div>
      ) : (
        <>
          {paginatedUsers.map((vendor) => (
            <VendorCard key={vendor._id} vendor={vendor} showActions={false} showDelete={true} />
          ))}
          {totalPages > 1 && usersPerPage !== 'all' && (
            <div className="flex items-center justify-center gap-2 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
              <Button onClick={() => setUsersCurrentPage(1)} disabled={usersCurrentPage === 1} variant="outline" size="sm" className="dark:border-gray-600 dark:text-white disabled:opacity-50">First</Button>
              <Button onClick={() => setUsersCurrentPage(p => Math.max(1, p - 1))} disabled={usersCurrentPage === 1} variant="outline" size="sm" className="dark:border-gray-600 dark:text-white disabled:opacity-50"><ChevronLeft className="w-4 h-4" /></Button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) pageNum = i + 1;
                else if (usersCurrentPage <= 3) pageNum = i + 1;
                else if (usersCurrentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                else pageNum = usersCurrentPage - 2 + i;
                return (
                  <Button key={pageNum} onClick={() => setUsersCurrentPage(pageNum)} variant={usersCurrentPage === pageNum ? "default" : "outline"} size="sm"
                    className={usersCurrentPage === pageNum ? "bg-[#FFD12D] text-[#252525] hover:bg-[#252525] hover:text-[#FFD12D]" : "dark:border-gray-600 dark:text-white"}>
                    {pageNum}
                  </Button>
                );
              })}
              <Button onClick={() => setUsersCurrentPage(p => Math.min(totalPages, p + 1))} disabled={usersCurrentPage === totalPages} variant="outline" size="sm" className="dark:border-gray-600 dark:text-white disabled:opacity-50"><ChevronRight className="w-4 h-4" /></Button>
              <Button onClick={() => setUsersCurrentPage(totalPages)} disabled={usersCurrentPage === totalPages} variant="outline" size="sm" className="dark:border-gray-600 dark:text-white disabled:opacity-50">Last</Button>
            </div>
          )}
        </>
      )}
    </>
  );
};
